import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Plus, Trash2, Loader2, ScanLine, MapPin } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import StepHeader from "./StepHeader";

export default function ProvidersStep() {
  const qc = useQueryClient();
  const [npi, setNpi] = useState("");
  const [lookup, setLookup] = useState(null);

  const { data: providers = [], isLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/providers")).data?.providers ?? [],
  });

  const lookupMut = useMutation({
    mutationFn: async (npi_number) =>
      (await api.post("/api/edi/note-to-claim/providers/lookup", { npi_number })).data,
    onSuccess: (data) => setLookup(data),
    onError: (e) => {
      setLookup(null);
      toast.error(describeError(e));
    },
  });

  const addMut = useMutation({
    mutationFn: async (payload) =>
      (await api.post("/api/edi/note-to-claim/providers", payload)).data,
    onSuccess: () => {
      toast.success("Provider added");
      setLookup(null);
      setNpi("");
      qc.invalidateQueries({ queryKey: ["providers"] });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (npiN) =>
      (await api.delete(`/api/edi/note-to-claim/providers/${npiN}`)).data,
    onSuccess: () => {
      toast.success("Provider removed");
      qc.invalidateQueries({ queryKey: ["providers"] });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const onLookup = (e) => {
    e.preventDefault();
    const clean = npi.replace(/\D/g, "");
    if (clean.length !== 10) {
      toast.error("NPI must be exactly 10 digits");
      return;
    }
    lookupMut.mutate(clean);
  };

  const onAdd = () => {
    if (!lookup) return;
    // Pass through the full lookup payload — the API uses the same shape.
    addMut.mutate(lookup);
  };

  return (
    <div className="flex flex-col gap-0 h-full fade-up">
      <StepHeader
        eyebrow="Account · Step 3 of 5"
        title="Providers · NPI · CAQH"
        subtitle="Look up each rendering provider's NPI in the national registry — we prefill name, taxonomy, license and address. Then add them to your tenant."
        done={providers.length > 0}
      />

      <div className="mt-6 rounded-[14px] bg-[var(--ink)] text-white px-4 py-3 flex items-center gap-3" style={{ boxShadow: "0 10px 26px rgba(0,0,0,.22)" }}>
        <ScanLine size={16} />
        <div className="text-[11.5px] flex-1">
          <strong className="font-semibold">NPPES Lens</strong> — paste a 10-digit NPI to fetch
          provider details directly from the NPI registry.
        </div>
      </div>

      <form onSubmit={onLookup} className="mt-4 flex items-end gap-4">
        <div className="flex flex-col gap-0.5 flex-1">
          <label className="lf-label">NPI number</label>
          <input
            className="lf-input"
            placeholder="1033110119"
            value={npi}
            data-testid={TID.npiInput}
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => setNpi(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={lookupMut.isPending}
          data-testid={TID.npiLookupBtn}
          className="btn-ink inline-flex items-center gap-1.5"
        >
          {lookupMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Search size={14} />}
          Look up
        </button>
      </form>

      {lookup ? (
        <div
          data-testid={TID.npiResult}
          className="mt-4 rounded-[14px] border border-black/[.08] bg-white/55 p-4 fade-up"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-[12px] bg-[var(--ink)] text-white text-[14px] font-bold flex items-center justify-center flex-shrink-0">
              {(lookup.provider_name || "?")
                .split(" ")
                .map((s) => s[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[var(--ink)]">{lookup.provider_name}</div>
              <div className="text-[10.5px] text-[var(--gray2)] mt-0.5">
                NPI {lookup.npi_number} · {lookup.provider_type}
              </div>
            </div>
            <button
              onClick={onAdd}
              disabled={addMut.isPending}
              data-testid={TID.providerAddBtn}
              className="btn-ink inline-flex items-center gap-1.5"
            >
              {addMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={14} />}
              Add to tenant
            </button>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
            <Row label="Address">
              <div className="flex items-start gap-1.5">
                <MapPin size={12} className="mt-0.5 text-[var(--gray2)] flex-shrink-0" />
                <span>
                  {[lookup.address?.address_1, lookup.address?.city, lookup.address?.state, lookup.address?.postal_code]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            </Row>
            <Row label="Primary taxonomy">
              {(lookup.taxonomies || []).find((t) => t.primary)?.desc || (lookup.taxonomies || [])[0]?.desc || "—"}
            </Row>
            <Row label="State">{lookup.state || "—"}</Row>
            <Row label="License">{(lookup.taxonomies || []).find((t) => t.primary)?.license || "—"}</Row>
          </div>
        </div>
      ) : null}

      <div className="mt-7 flex-1">
        <div className="text-[10px] font-bold tracking-[0.1em] uppercase text-[var(--gray2)] pb-2 border-b border-black/[.1] flex items-center justify-between">
          <span>Your providers</span>
          <span className="text-[var(--gray3)]">{providers.length} total</span>
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-[12px] text-[var(--gray2)]">Loading providers…</div>
        ) : providers.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-[var(--gray2)] bg-white/40 border border-dashed border-black/[.12] rounded-[14px] mt-3">
            <strong className="text-[var(--ink2)]">No providers yet.</strong> Look up your first NPI above.
          </div>
        ) : (
          <div data-testid={TID.providersList} className="mt-3 rounded-[14px] overflow-hidden border border-black/[.08] bg-white/55">
            {providers.map((p, i) => (
              <div
                key={p.id}
                data-testid={TID.providerRow(p.npi_number)}
                className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-black/[.06]" : ""}`}
              >
                <div className="w-10 h-10 rounded-full bg-black/[.07] border border-black/[.06] text-[var(--ink2)] font-bold text-[11px] flex items-center justify-center flex-shrink-0">
                  {(p.provider_name || "?")
                    .split(" ")
                    .map((s) => s[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--ink)] truncate">
                    {p.provider_name}
                  </div>
                  <div className="text-[10.5px] text-[var(--gray2)] mt-0.5 truncate">
                    NPI {p.npi_number} ·{" "}
                    {(p.taxonomies || []).find((t) => t.primary)?.desc ||
                      (p.taxonomies || [])[0]?.desc || "—"}
                    {p.state ? ` · ${p.state}` : ""}
                  </div>
                </div>
                {p.client_id || p.client_4digit_id ? (
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-[6px] bg-[rgba(52,199,89,.13)] text-[var(--green-deep)] border border-[rgba(52,199,89,.3)]">
                    Linked · {p.client_id || p.client_4digit_id}
                  </span>
                ) : (
                  <span className="text-[9px] font-semibold px-2 py-0.5 rounded-[6px] bg-black/[.05] text-[var(--gray1)] border border-black/[.08]">
                    Unlinked
                  </span>
                )}
                <button
                  type="button"
                  disabled={delMut.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove provider NPI ${p.npi_number}?`))
                      delMut.mutate(p.npi_number);
                  }}
                  className="text-[var(--gray3)] hover:text-[var(--amber)] transition p-1.5 rounded-md hover:bg-black/[.04]"
                  aria-label="Remove provider"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[9.5px] font-semibold tracking-[.05em] uppercase text-[var(--gray3)]">
        {label}
      </div>
      <div className="text-[12px] text-[var(--ink2)] mt-0.5">{children}</div>
    </div>
  );
}
